import { useState, useMemo, useEffect } from 'react';
import { MapContainer, TileLayer, ZoomControl, useMap } from 'react-leaflet';
import { useNavigate } from 'react-router-dom';
import { useSchoolsMap } from '@/hooks/use-schools';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Loader2 } from 'lucide-react';
import { usePreferencesStore } from '@/stores/preferences/preferences-provider';
import { useSidebar } from '@/components/ui/sidebar';
import { GeoJSONLayer } from './GeoJSONLayer';
import { MapHeader } from './MapHeader';
import 'leaflet/dist/leaflet.css';
import './map.css';

// Status color mapping
const STATUS_COLORS = {
  live: '#07c493',
  waitlist: '#ffba3b',
  suspended: '#EA3F58',
  unlaunched: '#ff9031',
};

interface SchoolFeature {
  type: 'Feature';
  geometry: {
    type: 'Point';
    coordinates: [number, number]; // [lng, lat]
  };
  properties: {
    schoolName: string;
    schoolTitle: string;
    status: string;
    studentsCount?: number;
    [key: string]: any;
  };
}

interface GeoJSONData {
  type: 'FeatureCollection';
  features: SchoolFeature[];
}

interface MapData {
  live?: GeoJSONData;
  waitlist?: GeoJSONData;
  suspended?: GeoJSONData;
  unlaunched?: GeoJSONData;
}

// Component to invalidate map size when sidebar changes
function MapResizeHandler() {
  const map = useMap();
  const { state: sidebarState, open } = useSidebar();

  useEffect(() => {
    // Invalidate immediately when transition starts
    map.invalidateSize({ pan: false });
    
    // Also invalidate during transition (multiple times for smooth adjustment)
    const timers = [
      setTimeout(() => map.invalidateSize({ pan: false }), 50),
      setTimeout(() => map.invalidateSize({ pan: false }), 100),
      setTimeout(() => map.invalidateSize({ pan: false }), 150),
      setTimeout(() => map.invalidateSize({ pan: false }), 200),
      setTimeout(() => map.invalidateSize({ pan: false }), 250),
    ];

    return () => timers.forEach(timer => clearTimeout(timer));
  }, [sidebarState, open, map]);

  return null;
}

export function MapPage() {
  const navigate = useNavigate();
  const themeMode = usePreferencesStore((s) => s.themeMode);
  const { state: sidebarState, isMobile } = useSidebar();
  const [filters] = useState<string>('');
  const [visibleStatuses, setVisibleStatuses] = useState<Set<string>>(
    new Set(['live', 'waitlist', 'suspended', 'unlaunched'])
  );

  const { data: mapData, isLoading, error } = useSchoolsMap(filters);

  // Calculate statistics
  const stats = useMemo(() => {
    if (!mapData) return [];
    
    return Object.entries(mapData).map(([status, geoJSON]: [string, any]) => ({
      name: status.charAt(0).toUpperCase() + status.slice(1),
      value: geoJSON?.features?.length || 0,
      status,
    }));
  }, [mapData]);

  // Toggle status visibility
  const toggleStatus = (status: string) => {
    setVisibleStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(status)) {
        next.delete(status);
      } else {
        next.add(status);
      }
      return next;
    });
  };

  // Handle feature click
  const handleFeatureClick = (feature: any) => {
    const schoolId = feature.properties?.id || feature.properties?.schoolName;
    if (schoolId) {
      navigate(`/dashboard/schools/${schoolId}`);
    }
  };

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center p-6">
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load map data. Please try again later.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div 
      className="fixed top-14 md:top-16 right-0 md:right-2 bottom-0 md:bottom-2 overflow-hidden transition-[left] duration-200 ease-linear"
      style={{
        left: isMobile 
          ? '0' 
          : (sidebarState === 'expanded' 
              ? 'var(--sidebar-width)' 
              : 'calc(var(--sidebar-width-icon) + 1rem + 0.5rem)')
      }}
    >
      {/* Map Header with Search and Create Button */}
      <MapHeader />

      {/* Loading indicator - only show when fetching data */}
      {isLoading && (
        <div className="absolute top-20 right-10 z-20 flex items-center gap-2 rounded-lg bg-background/95 p-3 shadow-lg backdrop-blur-sm">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <p className="text-sm font-medium text-muted-foreground">Loading schools...</p>
        </div>
      )}

      {/* Status Toggle Chips */}
      <div className="absolute right-10 top-6 z-20 flex gap-2 rounded-lg bg-background/95 p-2 shadow-xl backdrop-blur-sm">
        {Object.entries(STATUS_COLORS).map(([status, color]) => (
          <Button
            key={status}
            size="sm"
            variant={visibleStatuses.has(status) ? 'default' : 'ghost'}
            onClick={() => toggleStatus(status)}
            className="capitalize font-semibold transition-all"
            style={
              visibleStatuses.has(status)
                ? { 
                    backgroundColor: color, 
                    borderColor: color,
                    color: '#ffffff'
                  }
                : { 
                    opacity: 0.5,
                    borderColor: color,
                    borderWidth: '2px',
                    borderStyle: 'solid'
                  }
            }
          >
            <span 
              className="mr-1.5 inline-block h-2 w-2 rounded-full"
              style={{ backgroundColor: color }}
            />
            {status}
            {stats.find((s) => s.status === status) && (
              <span className="ml-1.5 rounded-full bg-black/20 px-1.5 py-0.5 text-xs font-bold">
                {stats.find((s) => s.status === status)?.value || 0}
              </span>
            )}
          </Button>
        ))}
      </div>

      {/* Map Container - Always render, data loads in background */}
      <MapContainer
        center={[38.526524, -96.730444]}
        zoom={4}
        minZoom={2}
        maxZoom={15}
        className="h-full w-full"
        style={themeMode === 'dark' ? { backgroundColor: 'oklch(0.195 0.025 252.3)' } : {}}
        zoomControl={false}
        scrollWheelZoom={true}
        doubleClickZoom={true}
        touchZoom={true}
        zoomSnap={0.5}
        zoomDelta={0.5}
        preferCanvas={true}
      >
          {/* Handle map resizing when sidebar toggles */}
          <MapResizeHandler />

          <TileLayer
            attribution='&copy; <a href="https://carto.com/">CARTO</a>'
            url={
              themeMode === 'dark'
                ? 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
                : 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png'
            }
            subdomains="abcd"
            maxZoom={19}
          />

          {/* Zoom control on bottom left */}
          <ZoomControl position="bottomleft" />

          {/* Render GeoJSON layers natively - one per status */}
          {/* Render in reverse order so live schools appear on top */}
          {mapData && ['unlaunched', 'suspended', 'waitlist', 'live'].map((status) => {
            const color = STATUS_COLORS[status as keyof typeof STATUS_COLORS];
            const geoJSON = mapData[status as keyof MapData];
            // Use feature count in key to prevent unnecessary recreation
            const featureCount = geoJSON?.features?.length || 0;
            return (
              <GeoJSONLayer
                key={`${status}-${featureCount}`}
                data={geoJSON}
                color={color}
                visible={visibleStatuses.has(status)}
                onFeatureClick={handleFeatureClick}
              />
            );
          })}
        </MapContainer>
    </div>
  );
}